import { fetchWithAuth, BASE_URL, getStoredTokens } from "../api/client";

/**
 * Meeting Bot Service - Recall.ai Integration
 * Handles meeting bot invitations, session management, and real-time updates
 */

// For SSE, we need the actual backend URL since EventSource doesn't go through proxy
const getBackendUrl = () => {
    // In production, use the API base URL
    if (process.env.NODE_ENV === 'production') {
        return process.env.REACT_APP_API_BASE || '';
    }
    // In development, use the proxy target directly for SSE
    return process.env.REACT_APP_API_BASE || 'http://localhost:8000';
};

export const MeetingService = {
    /**
     * Invite a bot to a meeting
     * POST /api/meeting-bot/invite
     *
     * @param {string} meetingUrl - Zoom, Meet, or Teams URL
     * @param {string} caseId - Optional case ID to link
     * @param {string} botName - Optional custom bot name
     */
    inviteBot: async (meetingUrl, caseId = null, botName = "CloudRealtor Meeting Assistant") => {
        const payload = {
            meeting_url: meetingUrl,
            bot_name: botName
        };

        if (caseId) {
            payload.case_id = caseId;
        }

        const response = await fetchWithAuth(`${BASE_URL}/api/meeting-bot/invite`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || "Failed to invite bot to meeting");
        }

        return response.json();
    },

    /**
     * List user's meeting sessions
     * GET /api/meeting-bot/sessions
     *
     * @param {number} limit - Number of sessions to return (default: 20)
     */
    getSessions: async (limit = 20) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/meeting-bot/sessions?limit=${limit}`, {
            headers: { accept: "application/json" }
        });

        if (!response.ok) {
            throw new Error("Failed to fetch meeting sessions");
        }

        const data = await response.json();
        return data.data || data;
    },

    /**
     * Get session details
     * GET /api/meeting-bot/sessions/{session_id}
     *
     * @param {string} sessionId - The session ID
     */
    getSession: async (sessionId) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/meeting-bot/sessions/${encodeURIComponent(sessionId)}`, {
            headers: { accept: "application/json" }
        });

        if (!response.ok) {
            throw new Error("Failed to fetch session details");
        }

        const data = await response.json();
        return data.data || data;
    },

    /**
     * Delete a meeting session
     * DELETE /api/meeting-bot/sessions/{session_id}
     *
     * @param {string} sessionId - The session ID to delete
     * @returns {Promise<Object>} Delete confirmation with session_id and deleted_at
     */
    deleteSession: async (sessionId) => {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/meeting-bot/sessions/${encodeURIComponent(sessionId)}`,
            {
                method: "DELETE",
                headers: { accept: "application/json" }
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || errorData.detail || "Failed to delete meeting session");
        }

        return response.json();
    },

    /**
     * Update session notes (summary and action items)
     * PUT /api/meeting-bot/sessions/{session_id}
     *
     * @param {string} sessionId - The session ID
     * @param {Object} updates - The updates to apply
     * @param {string} updates.summary - Updated summary text
     * @param {Array} updates.action_items - Updated action items
     * @param {string} updates.title - Updated title
     */
    updateSession: async (sessionId, updates) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/meeting-bot/sessions/${encodeURIComponent(sessionId)}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json"
            },
            body: JSON.stringify(updates)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || "Failed to update meeting notes");
        }

        const data = await response.json();
        return data.data || data;
    },

    /**
     * Get meeting results (action items)
     * GET /api/meeting-bot/sessions/{session_id}/results
     *
     * @param {string} sessionId - The session ID
     * @returns {Promise<Object|null>} Results or null if not ready
     */
    getResults: async (sessionId) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/meeting-bot/sessions/${encodeURIComponent(sessionId)}/results`, {
            headers: { accept: "application/json" }
        });

        if (response.status === 404) {
            // Not ready yet
            return null;
        }

        if (!response.ok) {
            throw new Error("Failed to fetch meeting results");
        }

        const data = await response.json();
        return data.data || data;
    },

    /**
     * Check bot status
     * GET /api/meeting-bot/status/{bot_id}
     *
     * @param {string} botId - The bot ID
     */
    getBotStatus: async (botId) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/meeting-bot/status/${encodeURIComponent(botId)}`, {
            headers: { accept: "application/json" }
        });

        if (!response.ok) {
            throw new Error("Failed to fetch bot status");
        }

        const data = await response.json();
        return data.data || data;
    },

    /**
     * Connect to SSE for real-time meeting updates
     * GET /api/meeting-bot/events
     *
     * @param {Function} onMessage - Callback for messages
     * @param {Function} onError - Callback for errors
     * @returns {EventSource|null} The EventSource instance or null if not available
     */
    connectToEvents: (onMessage, onError) => {
        // Get auth token
        const tokens = getStoredTokens();
        const token = tokens?.accessToken;

        if (!token) {
            console.warn("No auth token available for SSE connection");
            return null;
        }

        // Use direct backend URL for SSE (doesn't go through proxy)
        const backendUrl = getBackendUrl();
        const url = `${backendUrl}/api/meeting-bot/events?token=${encodeURIComponent(token)}`;

        try {
            const eventSource = new EventSource(url);

            eventSource.onopen = () => {
                console.log("SSE connection established");
            };

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    onMessage(data);
                } catch (e) {
                    console.error("Failed to parse SSE message:", e);
                }
            };

            eventSource.onerror = (error) => {
                // Only log once, don't spam console
                if (eventSource.readyState === EventSource.CLOSED) {
                    console.warn("SSE connection closed");
                } else {
                    console.warn("SSE connection error - will use polling fallback");
                }
                if (onError) {
                    onError(error);
                }
            };

            return eventSource;
        } catch (e) {
            console.warn("Failed to create SSE connection:", e);
            return null;
        }
    },

    /**
     * Poll for results (fallback if SSE disconnects)
     *
     * @param {string} sessionId - The session ID
     * @param {Function} onResult - Callback when results are ready
     * @param {number} interval - Polling interval in ms (default: 10000)
     * @param {number} maxAttempts - Max polling attempts (default: 60)
     */
    pollForResults: (sessionId, onResult, interval = 10000, maxAttempts = 60) => {
        let attempts = 0;
        let timeoutId = null;

        const poll = async () => {
            if (attempts >= maxAttempts) {
                console.warn("Max polling attempts reached");
                return;
            }

            attempts++;

            try {
                const results = await MeetingService.getResults(sessionId);
                if (results && results.status === 'done') {
                    onResult(results);
                    return;
                }
            } catch (e) {
                console.error("Polling error:", e);
            }

            timeoutId = setTimeout(poll, interval);
        };

        poll();

        // Return cleanup function
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }
};

/**
 * Bot status constants
 */
export const BotStatus = {
    JOINING: 'joining',
    IN_WAITING_ROOM: 'in_waiting_room',
    IN_CALL: 'in_call',
    CALL_ENDED: 'call_ended',
    PROCESSING: 'processing',
    DONE: 'done',
    ERROR: 'error'
};

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status) => {
    switch (status) {
        case BotStatus.JOINING:
            return 'Joining meeting...';
        case BotStatus.IN_WAITING_ROOM:
            return 'In waiting room';
        case BotStatus.IN_CALL:
            return 'Recording';
        case BotStatus.CALL_ENDED:
            return 'Call ended';
        case BotStatus.PROCESSING:
            return 'Processing transcript...';
        case BotStatus.DONE:
            return 'Completed';
        case BotStatus.ERROR:
            return 'Error';
        default:
            return status;
    }
};

/**
 * Get status color class
 */
export const getStatusColor = (status) => {
    switch (status) {
        case BotStatus.DONE:
            return 'emerald';
        case BotStatus.ERROR:
            return 'red';
        case BotStatus.IN_CALL:
        case BotStatus.PROCESSING:
            return 'amber';
        case BotStatus.JOINING:
        case BotStatus.IN_WAITING_ROOM:
        case BotStatus.CALL_ENDED:
            return 'sky';
        default:
            return 'slate';
    }
};
